import { appInitialLoad } from "@services/app";
import { Context } from "components/CMS/useStore";
import BlogDetail from "containers/CMS/blogDetail";
import BlogLayout from "layout/blog";
import { mdxToHtml } from "lib/mdx";
import { blogQuery, blogSlugsQuery } from "lib/sanity-queries";
import { getClient, sanityClient } from "lib/sanity-server";
import { Blog } from "lib/sanity-types";
import { ArticleJsonLd, NextSeo } from "next-seo";
import { fetchTemplates } from "services/template";

export default function BlogPage({
  blog,
  app,
  fetchData,
}: {
  blog: Blog;
} & { fetchData?: any; app: any }) {
  const seoAttr = getSeoAttr(blog);
  const articleJsonAttr = getArticleJsonAttr(blog);

  return (
    <Context.Provider
      value={{
        fetchData,
        app,
      }}
    >
      <BlogLayout blog={blog}>
        <NextSeo {...seoAttr} />
        <ArticleJsonLd {...articleJsonAttr} />
        <BlogDetail blog={blog} />
      </BlogLayout>
    </Context.Provider>
  );
}

export async function getStaticPaths() {
  const paths = await sanityClient.fetch(blogSlugsQuery);
  return {
    paths: paths.map((slug) => ({ params: { slug } })),
    fallback: "blocking",
  };
}

export async function getStaticProps({ params, preview = false }) {
  const { blog } = await getClient(preview).fetch(blogQuery, {
    slug: params.slug,
  });

  if (!blog) {
    return { notFound: true };
  }

  let fetchData = {};

  if (blog.fetchData?.length > 0) {
    fetchData = await dataFetch(blog.fetchData);
  }

  const { html, readingTime } = await mdxToHtml(blog.content);

  const app = await appInitialLoad({});

  return {
    props: {
      fetchData,
      app,
      blog: {
        ...blog,
        content: html,
        readingTime,
      },
    },
  };
}

async function dataFetch(fetchData) {
  let data: any = {};
  for (let i = 0; i < fetchData.length; i++) {
    const item = fetchData[i];
    if (item.key === "templates" && item.value) {
      const templates = await fetchTemplates({ ids: item.value }).then(
        (res) => {
          return res.templates;
        }
      );
      data.templates = templates;
    }
  }
  return data;
}

function getSeoAttr(blog: Blog) {
  const url = `https://www.printy6.com/blog/${blog.slug}`;

  return {
    title: blog.pageTitle,
    description: blog.description,
    canonical: url,
    openGraph: {
      type: "article",
      url,
      local: "en_US",
      title: blog.pageTitle,
      description: blog.description,
      article: {
        publishedTime: blog.date,
        modifiedTime: blog.date,
        section: "e-commerce",
        authors: ["https://www.facebook.com/printy6"],
        tags: [],
      },
      images: [
        {
          url: blog.coverImage,
          width: blog.coverImageMetadata.width,
          height: blog.coverImageMetadata.height,
        },
      ],
    },
    facebook: {
      appId: "681451528956886",
    },
    twitter: {
      domain: "www.printy6.com",
      site: "@printy6",
      title: blog.title,
      description: blog.description,
      card: "summary_large_image",
      image: blog.coverImage,
      label2: "Est. reading time",
      data2: `${Math.ceil(blog.readingTime)} minutes`,
    },
  };
}

function getArticleJsonAttr(blog: Blog) {
  return {
    url: `https://www.printy6.com/blog/${blog.slug}`,
    title: blog.title,
    images: [blog.coverImage],
    datePublished: blog.date,
    dateModified: blog.date,
    authorName: ["Printy6", blog.author.name],
    publisherName: "Printy6",
    publisherLogo: "https://www.printy6.com/logo.svg",
    description: blog.description,
  };
}
